/**
 * Simple Auth Context for Coach Cedar
 * Provides user authentication state for the research system
 */

import React, { useContext, useState, useEffect } from "react";
import { AuthContext, type AuthUser } from "./authSession";
import { trackAuth, trackEvent } from "../utils/analytics";
import { deviceFingerprinting } from "../services/deviceFingerprinting";
import { storage } from "../services/storage";
import { markForcedLogout } from "../services/authForcedLogoutMarker";
import { Platform } from "react-native";
import { API_BASE_URL } from "../utils/api"; // Environment-aware API URL
import { setCurrentUserIdCache } from "../utils/userStorage";
import { secureTokenStorage } from "../services/secureTokenStorage";
import { attemptSessionRecovery } from "../auth/sessionRecovery";
import { getApiBaseUrl } from "../utils/apiConfig";
import { registerAuthContextLogin, unregisterAuthContextLogin } from "../auth/authContextBridge";
import { verifyAuth } from "../services/authVerifyResolver";
// Cold-start optimistic restore (2026-05-25): the JWT local-expiry decision
// (previously an inline Buffer-based base64url decode here) now lives in a
// pure, unit-tested helper shared with index.tsx's fast-path.
import { isJwtExpired } from "../utils/jwtExpiry";
import { readBootCredentials, unreadableParts } from "../auth/bootCredentials";

type User = AuthUser;

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const validateAndRestoreSession = async () => {
      console.log('🔄 Starting session validation and restoration...');
      
      // 🔧 DEV MODE: Skip API validation on localhost to avoid backend dependency
      if (typeof window !== 'undefined' && window.location && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')) {
        console.log('🔧 DEV MODE: Skipping API validation on localhost - using local storage only');
        const savedUser = await storage.getItem("cedar_user");
        if (savedUser) {
          try {
            const parsedUser = JSON.parse(savedUser);
            setUser(parsedUser);
            setCurrentUserIdCache(parsedUser?.id ?? null);
          } catch (e) {
            console.warn('Failed to parse saved user:', e);
          }
        } else {
          // Create a development user for localhost if none exists
          console.log('🔧 DEV MODE: Creating development user for localhost');
          const devUser = {
            id: 'dev-user-localhost',
            name: 'Development User',
            email: 'dev@localhost.coachcedar.com'
          };
          await storage.setItem("cedar_user", JSON.stringify(devUser));
          setUser(devUser);
          setCurrentUserIdCache(devUser.id);
        }
        setIsLoading(false);
        return;
      }

      // Native: restore session from secure storage (Auth0 native flow stores there, not in storage)
      if (Platform.OS !== "web") {
        // Read once the keychain can answer: a launch with the phone locked
        // waits here instead of settling on "no user" (auth/bootCredentials.ts).
        const creds = await readBootCredentials();
        if (unreadableParts(creds).length > 0) {
          // On screen and still refused — a keychain fault, already reported.
          // No user this launch, and nothing cleared: the session is intact.
          console.error("❌ AUTH_CONTEXT_KEYCHAIN_UNREADABLE — no session restored; credentials kept");
          setIsLoading(false);
          return;
        }
        const secureUser = creds.userData.state === "present" ? creds.userData.value : null;
        const secureJwt = creds.cedarJwt.state === "present" ? creds.cedarJwt.value : null;
        if (secureUser?.id && secureJwt) {
          // Local expiry guard (extracted to the pure, tested isJwtExpired
          // helper). Three-valued:
          //   true  → expired      → clear + re-auth
          //   false → unexpired    → optimistic restore (no network)
          //   null  → undeterminable (malformed / no exp) → fall through to
          //           the network verify below (safety net for odd tokens).
          const expiryState = isJwtExpired(secureJwt);

          if (expiryState === true) {
            // ── A LOCALLY-EXPIRED JWT IS A REFRESH CUE, NOT A LOGOUT CUE ────
            // (2026-08-14)
            //
            // This branch used to clear immediately. Two things made that the
            // single most destructive line in the auth stack:
            //
            //   1. clearAll() deletes the Auth0 REFRESH token — the one
            //      credential that could have renewed the session silently.
            //   2. Being pure-local (a storage read plus a decode) it reliably
            //      WON the boot race against UnifiedAuth0Provider's
            //      network-first recovery, destroying that refresh token
            //      before the provider could use it.
            //
            // Net effect: every user whose 7-day JWT expired while the app was
            // closed was force-signed-out at next open instead of refreshed —
            // weekly, fleet-wide, by construction. Measured in production on
            // 2026-08-14: sub=810c13d3, age_s=605465 (7.0077 days).
            //
            // The original intent — "don't restore a session that will
            // instantly 401" — is preserved: we still never restore an expired
            // token. We just try to replace it first.
            const recovery = await attemptSessionRecovery();

            if (recovery.kind === 'recovered') {
              const restoredUser = {
                id: secureUser.id,
                name: secureUser.name,
                email: secureUser.email,
              };
              setUser(restoredUser);
              setCurrentUserIdCache(restoredUser.id);
              await storage.setItem("jwt_token", recovery.token);
              trackEvent("session_restored", { user_id: restoredUser.id, refreshed: true });
              console.log("✅ Session refreshed at boot for user:", restoredUser.id);
              setIsLoading(false);
              return;
            }

            // ONLY a credential Auth0 positively refused (or the absence of
            // one) ends the session. 'network' / 'backend' / 'no_client' are
            // transient — and 'no_client' specifically means the provider
            // hasn't wired the SDK yet, i.e. exactly the boot ordering that
            // used to make this branch destructive. Keeping the tokens lets
            // the provider's own recovery, or simply the next launch with
            // connectivity, heal the session silently.
            if (recovery.kind === 'terminal') {
              console.warn(`⚠️ JWT expired and refresh was refused (${recovery.failure}) — clearing session`);
              markForcedLogout('jwt_expired_local'); // E2
              await secureTokenStorage.clearAll();
              setIsLoading(false);
              return;
            }

            console.warn(`⚠️ JWT expired; refresh inconclusive (${recovery.failure}) — keeping tokens for retry`);
            setIsLoading(false);
            return;
          }

          if (expiryState === false) {
            // ── COLD-START OPTIMISTIC RESTORE (2026-05-25) ────────────────
            // A locally-valid (unexpired) JWT is enough to render the app
            // NOW. Blocking first paint on a GET /api/auth/me round-trip was
            // the ~1-2s green-splash hold reported on TestFlight cold starts.
            // We restore from secure storage and stop loading immediately;
            // NO network call gates the splash here. Server-side revocation
            // of an unexpired token is still caught — by index.tsx's
            // background reconcile (verifyAuth → unauthorized → clearAll +
            // login), exactly as the pre-fix careful path did, just off the
            // critical path. (The pre-fix code already restored the session
            // unconditionally on a network error, so trusting a locally-valid
            // token is strictly less optimistic than that fallback was.)
            const restoredUser = {
              id: secureUser.id,
              name: secureUser.name,
              email: secureUser.email,
            };
            setUser(restoredUser);
            setCurrentUserIdCache(restoredUser.id);
            // Mirror the JWT into unified storage (DataService / useMyPathLogic
            // read `jwt_token`). Local write (~1ms) — not a network hop.
            await storage.setItem("jwt_token", secureJwt);
            trackEvent("session_restored", { user_id: restoredUser.id, optimistic: true });
            console.log("✅ Session restored (optimistic, no network) for user:", restoredUser.id);
            setIsLoading(false);
            return;
          }
          // expiryState === null → malformed / unreadable token. Fall through
          // to the network verify below — the safety net for tokens we can't
          // decode locally.

          try {
            const baseUrl = getApiBaseUrl();
            const authAbort = new AbortController();
            const authTimeout = setTimeout(() => authAbort.abort(), 15000);
            // Fix: /api/auth/validate doesn't exist — use /api/auth/me which does
            const response = await fetch(`${baseUrl}/api/auth/me`, {
              method: "GET",
              headers: {
                Authorization: `Bearer ${secureJwt}`,
                "Content-Type": "application/json",
              },
              signal: authAbort.signal,
            });
            clearTimeout(authTimeout);
            if (response.ok) {
              const user = {
                id: secureUser.id,
                name: secureUser.name,
                email: secureUser.email,
              };
              setUser(user);
              setCurrentUserIdCache(user.id);
              await storage.setItem("jwt_token", secureJwt);
              trackEvent("session_restored", { user_id: user.id });
              console.log("✅ Session restored from secure storage for user:", user.id);
              setIsLoading(false);
              return;
            }
          } catch (error) {
            // Offline or network error: restore session anyway so app is usable
            console.warn("⚠️ Token validation failed (offline?), restoring session from secure storage:", error);
            const user = {
              id: secureUser.id,
              name: secureUser.name,
              email: secureUser.email,
            };
            setUser(user);
            setCurrentUserIdCache(user.id);
            await storage.setItem("jwt_token", secureJwt);
            setIsLoading(false);
            return;
          }
        }
      }
      
      // PWA CACHE FIX: Detect stuck authentication state from stale Auth0 cache
      // CONSERVATIVE approach - only clear if TRULY stale (>30 days)
      const auth0State =
        Platform.OS === "web" && typeof window !== "undefined" && typeof localStorage !== "undefined"
          ? localStorage.getItem(
              "@@auth0spajs@@::agMKpwVd5TWnjqua1ELYgX3dbxscEfZI::https://api.coachcedar.com::openid profile email offline_access"
            )
          : null;
      if (auth0State) {
        try {
          const parsed = JSON.parse(auth0State);
          // Only clear if cache is VERY old (> 30 days) - conservative approach
          const cacheAge = Date.now() - (parsed.decodedToken?.claims?.iat * 1000 || 0);
          const thirtyDays = 30 * 24 * 60 * 60 * 1000;
          
          // Only clear if truly stale (30+ days old)
          if (cacheAge > thirtyDays && cacheAge < (365 * 24 * 60 * 60 * 1000)) { // Max 365 days
            console.warn('⚠️ Detected very stale Auth0 cache (age:', Math.round(cacheAge / (24*60*60*1000)), 'days) - clearing...');
            if (Platform.OS === "web" && typeof window !== "undefined" && typeof localStorage !== "undefined") {
              localStorage.removeItem(
                "@@auth0spajs@@::agMKpwVd5TWnjqua1ELYgX3dbxscEfZI::https://api.coachcedar.com::openid profile email offline_access"
              );
              localStorage.removeItem("cedar_user");
              localStorage.removeItem("jwt_token");
            }
            console.log('✅ Stale cache cleared - will show login screen');
            setIsLoading(false);
            return;
          }
        } catch (e) {
          // Only clear on parse error - not on missing fields
          console.warn('⚠️ Corrupted Auth0 cache detected (parse error) - clearing:', e);
          if (Platform.OS === "web" && typeof window !== "undefined" && typeof localStorage !== "undefined") {
            localStorage.removeItem(
              "@@auth0spajs@@::agMKpwVd5TWnjqua1ELYgX3dbxscEfZI::https://api.coachcedar.com::openid profile email offline_access"
            );
            localStorage.removeItem("cedar_user");
            localStorage.removeItem("jwt_token");
          }
          setIsLoading(false);
          return;
        }
      }
      
      // First check for existing Coach Cedar session (platform-agnostic storage)
      const savedUser = await storage.getItem("cedar_user");
      const jwtToken = await storage.getItem("jwt_token");
      
      if (savedUser && jwtToken) {
        try {
          const parsedUser = JSON.parse(savedUser);
          // Only set user if it's a valid authenticated user (not auto-created demo)
          if (parsedUser.id && !parsedUser.id.includes('demo-user-')) {
            
            // Validate JWT token via the resilient resolver.
            //
            // AUTH-ME-RESILIENCE FIX (2026-05-20):
            // Pre-fix, any non-2xx response cleared `cedar_user` +
            // `jwt_token`. A transient 5xx from the ALB (the 2026-05-20
            // storm peaked at 178/10min) would force web users through
            // full re-auth on session restore. The resolver returns
            // 'verified' (restore) / 'unauthorized' (clear & let
            // AuthGuard re-login) / 'transient_failure' (restore anyway
            // — same offline-tolerant behavior as the existing
            // network-error catch path).
            const webAuthAbort = new AbortController();
            const webAuthTimeout = setTimeout(() => webAuthAbort.abort(), 15000);
            try {
              const authResult = await verifyAuth(API_BASE_URL, jwtToken, {
                signal: webAuthAbort.signal,
              });
              clearTimeout(webAuthTimeout);

              if (authResult.kind === 'verified') {
                // Token is valid, restore user session
                setUser(parsedUser);
                setCurrentUserIdCache(parsedUser.id);
                trackEvent("session_restored", { user_id: parsedUser.id });
                console.log("✅ Session restored for user:", parsedUser.id);
                setIsLoading(false);
                return;
              }

              if (authResult.kind === 'unauthorized') {
                // 401/403 — token is unambiguously rejected. Clear and
                // let AuthGuard redirect to login.
                console.warn(`⚠️ JWT token rejected (${authResult.status}) — clearing auth data`);
                await storage.removeItem("cedar_user");
                await storage.removeItem("jwt_token");
                setIsLoading(false);
                return;
              }

              // kind === 'transient_failure'
              // Backend can't tell us right now. KEEP TOKENS and
              // restore the session offline-tolerantly. The next
              // user-driven request will surface a real failure if
              // the token genuinely is dead.
              console.warn(`⚠️ Auth verify transient_failure (${authResult.reason}) — restoring session offline-tolerantly`);
              setUser(parsedUser);
              setCurrentUserIdCache(parsedUser.id);
              trackEvent("session_restored_offline", { user_id: parsedUser.id, reason: authResult.reason });
              setIsLoading(false);
              return;
            } catch (error) {
              clearTimeout(webAuthTimeout);
              // AbortError (15s timeout) or unexpected throw from the
              // resolver. Treat the same as a transient_failure: keep
              // tokens, restore session. Matches pre-fix behavior for
              // genuine network errors.
              console.warn("⚠️ Could not validate token (offline?), restoring session anyway:", error);
              setUser(parsedUser);
              setCurrentUserIdCache(parsedUser.id);
              trackEvent("session_restored_offline", { user_id: parsedUser.id });
              setIsLoading(false);
              return;
            }
          } else {
            // Clean up any old demo users
            await storage.removeItem("cedar_user");
            await storage.removeItem("jwt_token");
            await attemptSilentAuth();
          }
        } catch (error) {
          console.error("Failed to parse saved user:", error);
          await storage.removeItem("cedar_user");
          await storage.removeItem("jwt_token");
          await attemptSilentAuth();
        }
      } else {
        // No existing session, try Auth0 silent authentication
        console.log("🔄 No existing session found, attempting Auth0 silent authentication...");
        await attemptSilentAuth();
      }
    };

    const attemptSilentAuth = async () => {
      // CRITICAL FIX: AuthContext should NOT attempt Auth0 silent auth
      // Auth0 silent auth is handled by EnhancedAuthGuard which has access to useAuth0 hooks
      // This context just checks for existing cedar_user/jwt_token
      // If none exist, let EnhancedAuthGuard handle the Auth0 flow
      console.log("ℹ️ No existing Coach Cedar session found");
      console.log("🔄 Auth0 authentication will be handled by EnhancedAuthGuard");
      setIsLoading(false);
    };
    
    validateAndRestoreSession();
  }, []);

  const login = async (newUser: User, skipDeviceRegistration = false) => {
    setUser(newUser);
    setCurrentUserIdCache(newUser.id);
    try {
      await storage.setItem("cedar_user", JSON.stringify(newUser));
    } catch (storageErr) {
      // Storage failure must not throw — setUser() already updated React state.
      // The user will be logged in for this session; persistence may fail on next cold start.
      console.warn("⚠️ AuthContext.login: Failed to persist cedar_user to storage:", storageErr);
    }

    // Track login event
    trackAuth("login", newUser.id);
    
    // Register device for security tracking (skip if already done during Auth0 flow)
    if (!skipDeviceRegistration) {
      try {
        const deviceInfo = deviceFingerprinting.getDeviceInfo();
        
        const token = await storage.getItem("jwt_token");
        const response = await fetch(`${API_BASE_URL}/api/devices/register`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token || ""}`,
          },
          body: JSON.stringify(deviceInfo)
        });
        
        if (response.ok) {
          const result = await response.json();
          console.log("📱 Device registered via AuthContext:", result.device_id);
        } else {
          console.warn("⚠️ Device registration failed:", response.status);
        }
      } catch (error) {
        console.warn("⚠️ Device registration error:", error);
        // Don't block login if device registration fails
      }
    } else {
      console.log("📱 Device registration skipped - already handled by Auth0 flow");
    }
  };

  const logout = async () => {
    const userId = user?.id;
    if (user) {
      trackAuth("logout", user.id);
    }
    setUser(null);
    setCurrentUserIdCache(null);
    await storage.removeItem("cedar_user");
    await storage.removeItem("jwt_token");
    // S1-9: Clear purchase_completed flag on logout so a re-login doesn't carry
    // a stale "purchase done" signal that blocks the trial gate from showing.
    await storage.removeItem("purchase_completed");

    // Clear all user-specific data from local storage.
    // First-time flags (health disclaimer, welcome letter) are now tracked in
    // the database (user_agreement_log table), so no skip list is needed.
    if (userId && Platform.OS === "web" && typeof localStorage !== "undefined") {
      Object.keys(localStorage).forEach((key) => {
        if (key.includes(userId) || key.startsWith(`${userId}_`)) {
          localStorage.removeItem(key);
        }
      });
    } else if (userId) {
      // Native: clear user-specific keys from unified storage
      try {
        const keys = await storage.getAllKeys();
        for (const key of keys) {
          if (key.includes(userId) || key.startsWith(`${userId}_`)) {
            await storage.removeItem(key);
          }
        }
      } catch (e) {
        console.warn("Logout: failed to clear user storage keys", e);
      }
    }
  };

  // Register login with the auth bridge so UnifiedAuth0Provider (which wraps
  // AuthProvider and can't use useAuth) can push the user into AuthContext
  // immediately after Auth0 login — no SecureStore polling needed.
  useEffect(() => {
    registerAuthContextLogin(login);
    return () => unregisterAuthContextLogin();
  }, []); // login identity changes every render, but the bridge ref always calls the latest closure

  const value = {
    user,
    login,
    logout,
    isLoading
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 